/**
 * `<plp-countdown>` — обратный отсчёт.
 *
 * В шаблоне ищет спаны `<span data-pl-days|hours|min|sec>` и обновляет
 * их textContent каждую секунду. Если шаблона нет — рендерит дефолтный.
 *
 * По истечении заменяет содержимое на `expired-text` или редиректит
 * (`expired-redirect`). В edit-mode тикает но не редиректит.
 */

import { isEditMode } from '../core/edit-mode.js';
import { t } from '../i18n/index.js';

function defaultTemplate(): string {
  return (
    `<span data-pl-days></span>${t('countdown.dayShort')} ` +
    '<span data-pl-hours></span>:<span data-pl-min></span>:<span data-pl-sec></span>'
  );
}

export class PlpCountdown extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['end', 'expired-text', 'expired-redirect'];
  }

  private _timer?: number;
  private _expired = false;

  connectedCallback(): void {
    if (this.children.length === 0 && this.textContent?.trim() === '') {
      this.innerHTML = defaultTemplate();
    }
    this.tick();
    this._timer = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback(): void {
    if (this._timer !== undefined) window.clearInterval(this._timer);
  }

  attributeChangedCallback(): void {
    this._expired = false;
    if (this.isConnected) this.tick();
  }

  private tick(): void {
    const end = this.endTime();
    if (end === null) return;
    const now = Date.now();
    const remainingMs = end - now;

    if (remainingMs <= 0) {
      if (this._expired) return;
      this._expired = true;
      this.handleExpired();
      return;
    }

    const totalSec = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const min = Math.floor((totalSec % 3600) / 60);
    const sec = totalSec % 60;

    this.set('days', days);
    this.set('hours', hours);
    this.set('min', min);
    this.set('sec', sec);
  }

  private set(name: string, value: number): void {
    const nodes = this.querySelectorAll(`[data-pl-${name}]`);
    const text = name === 'days' ? String(value) : value.toString().padStart(2, '0');
    nodes.forEach((n) => (n.textContent = text));
  }

  private endTime(): number | null {
    const raw = this.getAttribute('end');
    if (!raw) return null;
    const t = Date.parse(raw);
    return Number.isFinite(t) ? t : null;
  }

  private handleExpired(): void {
    if (this._timer !== undefined) {
      window.clearInterval(this._timer);
      this._timer = undefined;
    }
    const text = this.getAttribute('expired-text') ?? t('countdown.expired');
    this.innerHTML = `<span class="plp-countdown-expired">${escapeHtml(text)}</span>`;

    const redirect = this.getAttribute('expired-redirect');
    if (redirect && !isEditMode()) {
      try {
        location.href = redirect;
      } catch {
        /* ignore */
      }
    }
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
